import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { 
  Check, 
  X, 
  ChevronDown, 
  ChevronUp, 
  Shield, 
  CreditCard, 
  Sparkles,
  Users,
  Star,
  Zap,
  ArrowRight,
  Clock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { Button } from '../components/ui';
import { useToast } from '../components/ui/Toast';

const PricingPage = () => {
  const { user } = useAuth();
  const { isPro } = useChat();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [openFaq, setOpenFaq] = useState(null);

  const handleUpgrade = (plan) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    // TODO: Integrate Stripe Checkout
    showToast('Stripe integration coming soon! Add your Stripe keys to enable payments.', 'info');
  };

  const freePlanFeatures = [
    { text: '10 calculations per month', included: true },
    { text: 'All 6 material types', included: true },
    { text: 'Continue conversations', included: true },
    { text: 'Save calculations', included: true },
    { text: 'Basic cost estimates', included: true },
    { text: 'Email support', included: true },
  ];

  // Pro-only features (exclusive to Pro plan)
  const proOnlyFeatures = [
    { text: 'Unlimited calculations', included: true, highlight: true },
    { text: 'Priority support', included: true, highlight: true },
    { text: 'No advertisements', included: true, highlight: true },
    { text: 'Export to PDF', included: true, highlight: true },
    { text: 'Advanced cost breakdowns', included: true, highlight: true },
    { text: 'Material price comparisons', included: true, highlight: true },
  ];

  // Common features (shared with Free plan)
  const proCommonFeatures = [
    { text: 'All 6 material types', included: true },
    { text: 'Continue conversations', included: true },
    { text: 'Save calculations', included: true },
  ];

  const faqs = [
    {
      question: 'What counts as a calculation?',
      answer: 'A calculation is counted each time you start a new conversation with the AI. Follow-up questions within the same conversation don\'t count as additional calculations.',
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! You can cancel your Pro subscription at any time. You\'ll continue to have access to Pro features until the end of your billing period.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe.',
    },
    {
      question: 'Is there a refund policy?',
      answer: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied with Pro, contact us within 30 days for a full refund.',
    },
    {
      question: 'What happens when my free calculations run out?',
      answer: 'Your calculation count resets on the 1st of each month. You can also upgrade to Pro for unlimited calculations anytime.',
    },
    {
      question: 'Can I export my calculations?',
      answer: 'PDF export is available on the Pro plan. Free users can copy their results manually.',
    },
  ];

  const testimonials = [
    {
      name: 'Mike R.',
      role: 'General Contractor',
      content: 'Pro pays for itself on the first project. Unlimited calculations is a game-changer.',
      avatar: 'M',
    },
    {
      name: 'Sarah T.',
      role: 'Interior Designer',
      content: 'The accuracy has saved me thousands in material costs this year alone.',
      avatar: 'S',
    },
    {
      name: 'James L.',
      role: 'Project Manager',
      content: 'Worth every penny. My entire team relies on CalcBuildAI daily.',
      avatar: 'J',
    },
  ];

  const annualPrice = 99;
  const monthlyPrice = 12;
  const monthlyEquivalent = Math.round(annualPrice / 12);
  const annualSavings = (monthlyPrice * 12) - annualPrice;
  const savingsPercent = Math.round((annualSavings / (monthlyPrice * 12)) * 100);

  return (
    <>
      <Helmet>
        <title>Pricing - CalcBuildAI</title>
        <meta name="description" content="Choose the right plan for your construction calculations. Free tier with 10 calculations per month or Pro with unlimited access." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-white">
        {/* Header */}
        <section className="py-16 text-center">
          <div className="max-w-4xl mx-auto px-4">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              Trusted by 10,000+ builders
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Start free, upgrade when you need more
            </p>

            {/* Billing toggle with savings highlight */}
            <div className="inline-flex items-center gap-1 bg-gray-100 p-1.5 rounded-xl">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 ${
                  billingPeriod === 'monthly' 
                    ? 'bg-white text-gray-900 shadow-md' 
                    : 'text-gray-700 hover:text-gray-900'
                }`}
                aria-label="Switch to monthly billing"
                aria-pressed={billingPeriod === 'monthly'}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 ${
                  billingPeriod === 'annual' 
                    ? 'bg-white text-gray-900 shadow-md' 
                    : 'text-gray-700 hover:text-gray-900'
                }`}
                aria-label="Switch to annual billing"
                aria-pressed={billingPeriod === 'annual'}
              >
                Annual
                <span className="text-xs bg-success-600 text-white px-2 py-0.5 rounded-full font-bold">
                  -{savingsPercent}%
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-16">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              {/* Free Plan */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 flex flex-col">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                  <p className="text-gray-600">Perfect for occasional projects</p>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-gray-900">$0</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {freePlanFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature.text}</span>
                    </li>
                  ))}
                  <li className="flex items-center gap-3 text-gray-400">
                    <X className="w-5 h-5 flex-shrink-0" />
                    <span>Export to PDF</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-400">
                    <X className="w-5 h-5 flex-shrink-0" />
                    <span>Advanced cost breakdowns</span>
                  </li>
                </ul>

                <Button
                  onClick={() => !user && navigate('/signup')}
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  disabled={user && !isPro}
                >
                  {user && !isPro ? 'Current Plan' : 'Get Started Free'}
                </Button>
              </div>

              {/* Pro Plan - Highlighted */}
              <div className="relative bg-gradient-to-b from-blue-600 to-indigo-700 rounded-2xl p-8 flex flex-col text-white shadow-2xl shadow-blue-500/30">
                {/* Popular badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-1.5 shadow-lg">
                    <Star className="w-4 h-4 fill-current" />
                    MOST POPULAR
                  </div>
                </div>

                <div className="mb-6 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-2xl font-bold">Pro</h3>
                    <Sparkles className="w-5 h-5 text-yellow-300" />
                  </div>
                  <p className="text-blue-100">For professionals and frequent users</p>
                </div>
                
                <div className="mb-6">
                  {billingPeriod === 'annual' ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-5xl font-bold">${monthlyEquivalent}</span>
                        <span className="text-blue-200">/month</span>
                      </div>
                      <div className="text-blue-200 text-sm mt-1">
                        ${annualPrice} billed annually • Save ${annualSavings}/year
                      </div>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold">${monthlyPrice}</span>
                      <span className="text-blue-200">/month</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {/* Pro-only features with high-contrast styling */}
                  {proOnlyFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        feature.highlight 
                          ? 'bg-gradient-to-r from-yellow-500 to-amber-600' 
                          : 'bg-accent-600'
                      }`}>
                        <Check className={`w-3 h-3 ${feature.highlight ? 'text-gray-900' : 'text-white'}`} />
                      </div>
                      <span className={feature.highlight ? 'font-bold text-white' : 'font-semibold text-white'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                  
                  {/* Visual separator */}
                  <li className="mt-4 pt-4 border-t border-white/20">
                    <div className="text-xs text-white/70 font-medium mb-2">Also included in Free plan</div>
                  </li>
                  
                  {/* Common features with muted styling */}
                  {proCommonFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-400/60">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-white/90">
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleUpgrade('pro')}
                  size="lg"
                  variant="light"
                  fullWidth
                  disabled={isPro}
                  icon={!isPro ? ArrowRight : undefined}
                  iconPosition="right"
                >
                  {isPro ? 'Current Plan' : 'Upgrade to Pro'}
                </Button>

                {/* Urgency indicator */}
                {!isPro && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-blue-200 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>Join 2,500+ Pro users this month</span>
                  </div>
                )}
              </div>
            </div>

            {/* Trust signals */}
            <div className="mt-12 flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2 text-gray-600">
                <Shield className="w-5 h-5 text-green-500" />
                <span>30-day money-back guarantee</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <CreditCard className="w-5 h-5 text-blue-500" />
                <span>Secure payment via Stripe</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
              What Pro Users Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Compare Plans
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-900 w-28">Free</th>
                    <th className="text-center py-4 px-4 w-28">
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                        <Sparkles className="w-3 h-3" />
                        Pro
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Calculations per month', free: '10', pro: '∞ Unlimited' },
                    { feature: 'Material types', free: 'All 6', pro: 'All 6' },
                    { feature: 'Save calculations', free: true, pro: true },
                    { feature: 'Continue conversations', free: true, pro: true },
                    { feature: 'Cost estimates', free: 'Basic', pro: 'Advanced' },
                    { feature: 'Export to PDF', free: false, pro: true },
                    { feature: 'Advertisements', free: 'Yes', pro: 'None' },
                    { feature: 'Support response time', free: '48 hours', pro: '< 4 hours' },
                    { feature: 'Material price comparisons', free: false, pro: true },
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4 text-gray-700">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {typeof row.free === 'boolean' ? (
                          row.free ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          <span className="text-gray-600 text-sm">{row.free}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {typeof row.pro === 'boolean' ? (
                          row.pro ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          <span className="text-blue-600 font-semibold text-sm">{row.pro}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      openFaq === idx ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {openFaq === idx ? (
                        <ChevronUp className="w-5 h-5 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openFaq === idx ? 'max-h-40' : 'max-h-0'
                  }`}>
                    <div className="px-6 pb-4 text-gray-600">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600 relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Calculate Smarter?
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Join thousands of builders saving time and reducing waste
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                size="lg"
                variant="light"
              >
                Try Free Calculator
              </Button>
              <Button
                onClick={() => handleUpgrade('pro')}
                size="lg"
                variant="light-outline"
                icon={Sparkles}
              >
                Upgrade to Pro
              </Button>
            </div>
            <p className="text-blue-200 text-sm mt-6 flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" />
              Secure payment powered by Stripe • Cancel anytime
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default PricingPage;
